import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const DonationSection = ({ charity }) => {
  const [donationAmount, setDonationAmount] = useState('');
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState('monthly');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [dedicationType, setDedicationType] = useState('none');
  const [dedicationName, setDedicationName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const quickAmounts = [25, 50, 100, 250, 500, 1000];
  const frequencies = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'annually', label: 'Annually' }
  ];

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setDonationAmount(amount?.toString());
  };

  const handleCustomAmount = (e) => {
    const value = e?.target?.value;
    setDonationAmount(value);
    setSelectedAmount(null);
  };

  const handleDonate = async () => {
    if (!donationAmount || parseFloat(donationAmount) <= 0) return;

    setIsProcessing(true);
    
    // Simulate donation processing
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsProcessing(false);
    
    // Reset form
    setDonationAmount('');
    setSelectedAmount(null);
    setIsRecurring(false);
    setIsAnonymous(false);
    setDedicationType('none');
    setDedicationName('');
    
    alert(`Thank you for your donation of $${donationAmount}!`);
  };

  const totalAmount = parseFloat(donationAmount) || 0;
  const processingFee = totalAmount * 0.029; // 2.9% processing fee
  const finalAmount = totalAmount + processingFee;

  return (
    <div id="donation-section" className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="Heart" size={24} className="text-primary" />
        <h2 className="text-xl font-semibold text-card-foreground">Make a Donation</h2>
      </div>
      <div className="space-y-6">
        {/* Quick Amount Selection */}
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-3">
            Choose Amount
          </label>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {quickAmounts?.map((amount) => (
              <Button
                key={amount}
                variant={selectedAmount === amount ? "default" : "outline"}
                size="sm"
                onClick={() => handleAmountSelect(amount)}
                className="font-mono"
              >
                ${amount}
              </Button>
            ))}
          </div>
        </div>

        {/* Custom Amount */}
        <div>
          <Input
            label="Custom Amount"
            type="number"
            placeholder="Enter amount"
            value={donationAmount}
            onChange={handleCustomAmount}
            className="font-mono"
          />
        </div>

        {/* Recurring Donation */}
        <div className="space-y-3">
          <Checkbox
            label="Make this a recurring donation"
            checked={isRecurring}
            onChange={(e) => setIsRecurring(e?.target?.checked)}
          />
          
          {isRecurring && (
            <div className="ml-6">
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Frequency
              </label>
              <div className="flex flex-wrap gap-2">
                {frequencies?.map((freq) => (
                  <Button
                    key={freq?.value}
                    variant={frequency === freq?.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFrequency(freq?.value)}
                  >
                    {freq?.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Dedication Options */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-card-foreground">
            Dedication (Optional)
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={dedicationType === 'none' ? "default" : "outline"}
              size="sm"
              onClick={() => setDedicationType('none')}
            >
              None
            </Button>
            <Button
              variant={dedicationType === 'honor' ? "default" : "outline"}
              size="sm"
              onClick={() => setDedicationType('honor')}
            >
              In Honor Of
            </Button>
            <Button
              variant={dedicationType === 'memory' ? "default" : "outline"}
              size="sm"
              onClick={() => setDedicationType('memory')}
            >
              In Memory Of
            </Button>
          </div>
          
          {dedicationType !== 'none' && (
            <Input
              placeholder="Enter name"
              value={dedicationName}
              onChange={(e) => setDedicationName(e?.target?.value)}
            />
          )}
        </div>

        {/* Anonymous Donation */}
        <Checkbox
          label="Make this donation anonymous"
          description="Your name will not be displayed publicly"
          checked={isAnonymous}
          onChange={(e) => setIsAnonymous(e?.target?.checked)}
        />

        {/* Donation Summary */}
        {totalAmount > 0 && (
          <div className="bg-muted rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span>Donation Amount:</span>
              <span className="font-mono">${totalAmount?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Processing Fee (2.9%):</span>
              <span className="font-mono">${processingFee?.toFixed(2)}</span>
            </div>
            <hr className="border-border" />
            <div className="flex justify-between font-semibold">
              <span>Total:</span>
              <span className="font-mono">${finalAmount?.toFixed(2)}</span>
            </div>
            {isRecurring && (
              <div className="text-xs text-muted-foreground">
                This amount will be charged {frequency}
              </div>
            )}
          </div>
        )}

        {/* Donate Button */}
        <Button
          onClick={handleDonate}
          disabled={!donationAmount || parseFloat(donationAmount) <= 0 || isProcessing}
          loading={isProcessing}
          size="lg"
          className="w-full"
          iconName="Heart"
          iconPosition="left"
        >
          {isProcessing ? 'Processing...' : `Donate $${totalAmount?.toFixed(2)}`}
        </Button>

        {/* Trust Indicators */}
        <div className="flex items-center justify-center space-x-4 text-xs text-muted-foreground">
          <div className="flex items-center space-x-1">
            <Icon name="Shield" size={12} />
            <span>Secure Payment</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="FileText" size={12} />
            <span>Tax Deductible</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Clock" size={12} />
            <span>Instant Receipt</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonationSection;